/**
 * Admin — LLM Connector.
 *
 * Operating surface for the remote MCP connector, in the TaskManagement shape: header, stat cards,
 * then tabs. Everything is shown as a person (avatar, name, email) — the console never shows a
 * user id, because nobody can tell who an id belongs to.
 *
 *   People    — who may connect (the ConnectorUser role), added by searching the directory.
 *   Sessions  — every grant on the system, with Disconnect. Bites on the session's next call.
 *   Audit log — one row per tool call, reads included, arguments redacted of free text.
 *   Clients   — OAuth clients that registered (usually one per person who added the connector).
 *   Switches  — the master Enabled flag and one switch per tool module. Fail closed on the server.
 *
 * Who can use it: the Connector.Admin right (granted to Admin by migration 245).
 * Route: /admin/connector
 */

import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  IconPlugConnected, IconPlugConnectedX, IconRefresh, IconToggleLeft, IconUsers, IconListDetails, IconApps,
  IconAlertTriangle, IconUserPlus, IconUserMinus, IconActivity, IconEye, IconPower,
} from '@tabler/icons-react';
import { usePageFilters } from '@/stores';
import { LayoutContainer } from '@/components/ui/layout';
import {
  Stack, Group, Button, Text, Card, Badge, Alert, Loader, Code, Switch, Tabs, Tooltip, SimpleGrid, Select, Skeleton, Menu, EmptyState, modal,
} from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { FadeIn } from '@/components/animations';
import { DataTable } from '@/components/advanced/data';
import type { DataTableColumn } from '@/components/advanced/data';
import { useConnectorService } from '@actschurch/ccms-sdk';
import type { ConnectorClient, ConnectorGrant, ConnectorLog, ConnectorPerson, ConnectorSetting } from '@actschurch/ccms-sdk';
import { formatRelative, formatStamp, isWriteScope, scopeLabel, scopeList, stampToDate } from '../../settings/connected-apps/connectorFormat';
import { PageHeader, StatsCard, FilterToolbar, ResultsCount, FilterDrawer, PersonCell, optionsFrom } from './components';
import { AddPersonModal } from './AddPersonModal';
import { KEYS, LOG_WINDOW } from './keys';

type Tab = 'people' | 'sessions' | 'audit' | 'clients' | 'switches';

const SESSION_DEFAULTS = { search: '', status: 'live', client: '', scope: '' };
const AUDIT_DEFAULTS = { search: '', person: '', module: '', tool: '', result: '' };

function personLabel(name?: string | null, email?: string | null) { return name?.trim() || email || 'Unknown user'; }
function matches(haystack: (string | null | undefined)[], needle: string) {
  const q = needle.trim().toLowerCase();
  return !q || haystack.some((h) => (h ?? '').toLowerCase().includes(q));
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function ConnectorAdminPage() {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>('people');
  const [adding, setAdding] = useState(false);

  const settings = useQuery({ queryKey: KEYS.settings, queryFn: () => service.getSettings() });
  const people = useQuery({ queryKey: KEYS.people, queryFn: () => service.getPeople() });
  const grants = useQuery({ queryKey: KEYS.grants, queryFn: () => service.getAllGrants(false, 500) });
  const logs = useQuery({ queryKey: KEYS.logs, queryFn: () => service.getLogsRaw({ skip: 0, take: LOG_WINDOW }) });

  const master = settings.data?.find((s) => s.name === 'Enabled');
  const stats = useMemo(() => {
    const since = Date.now() - 24 * 3600 * 1000;
    const recent = (logs.data?.data ?? []).filter((l) => (stampToDate(l.logDateTime)?.getTime() ?? 0) >= since);
    return {
      people: people.data?.length ?? 0,
      live: (grants.data ?? []).filter((g) => g.isLive).length,
      calls: recent.length,
      failures: recent.filter((l) => !l.success && l.statusCode !== 428).length,
    };
  }, [people.data, grants.data, logs.data]);

  const refreshAll = () => {
    for (const key of [KEYS.settings, KEYS.people, KEYS.grants, KEYS.logs, KEYS.clients]) queryClient.invalidateQueries({ queryKey: key });
  };
  const anyFetching = settings.isFetching || people.isFetching || grants.isFetching || logs.isFetching;
  const enabledIds = useMemo(() => new Set((people.data ?? []).map((p) => p.userId)), [people.data]);

  if (settings.isLoading && people.isLoading) {
    return (
      <LayoutContainer><FadeIn><Stack gap="lg">
        <Skeleton height={36} width={260} />
        <SimpleGrid cols={{ base: 2, md: 4 }}>{[1, 2, 3, 4].map((i) => <Skeleton key={i} height={90} radius="md" />)}</SimpleGrid>
        <Skeleton height={50} radius="md" /><Skeleton height={400} radius="md" />
      </Stack></FadeIn></LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          <PageHeader
            title={<Group gap="sm" wrap="nowrap">LLM Connector {master && <Badge size="lg" variant="light" color={master.isOn ? 'green' : 'red'}>{master.isOn ? 'ON' : 'OFF'}</Badge>}</Group>}
            subtitle="Lets staff drive CCMS from an AI assistant as themselves. Nothing here grants reach beyond a person's own CCMS rights — these controls only narrow."
            actions={<>
              <Button variant="light" leftSection={<IconRefresh size={16} />} loading={anyFetching} onClick={refreshAll}>Refresh</Button>
              <Button leftSection={<IconUserPlus size={16} />} onClick={() => setAdding(true)}>Add person</Button>
            </>}
          />

          {settings.error && <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Could not load the connector">{settings.error instanceof Error ? settings.error.message : 'Try again.'}</Alert>}

          <SimpleGrid cols={{ base: 2, md: 4 }}>
            <StatsCard icon={<IconUsers size={20} />} color="blue" value={stats.people} label="People enabled" active={tab === 'people'} onClick={() => setTab('people')} />
            <StatsCard icon={<IconPlugConnected size={20} />} color="green" value={stats.live} label="Live sessions" active={tab === 'sessions'} onClick={() => setTab('sessions')} />
            <StatsCard icon={<IconActivity size={20} />} color="violet" value={stats.calls} label="Tool calls, last 24h" active={tab === 'audit'} onClick={() => setTab('audit')} />
            <StatsCard icon={<IconAlertTriangle size={20} />} color="red" value={stats.failures} label="Failures, last 24h" />
          </SimpleGrid>

          <Tabs value={tab} onChange={(v) => setTab((v as Tab) ?? 'people')} keepMounted={false}>
            <Tabs.List>
              <Tabs.Tab value="people" leftSection={<IconUsers size={16} />}>People</Tabs.Tab>
              <Tabs.Tab value="sessions" leftSection={<IconPlugConnected size={16} />}>Sessions</Tabs.Tab>
              <Tabs.Tab value="audit" leftSection={<IconListDetails size={16} />}>Audit log</Tabs.Tab>
              <Tabs.Tab value="clients" leftSection={<IconApps size={16} />}>Clients</Tabs.Tab>
              <Tabs.Tab value="switches" leftSection={<IconToggleLeft size={16} />}>Switches</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="people" pt="md"><PeopleTab onAdd={() => setAdding(true)} onViewSessions={() => setTab('sessions')} /></Tabs.Panel>
            <Tabs.Panel value="sessions" pt="md"><SessionsTab /></Tabs.Panel>
            <Tabs.Panel value="audit" pt="md"><AuditTab /></Tabs.Panel>
            <Tabs.Panel value="clients" pt="md"><ClientsTab /></Tabs.Panel>
            <Tabs.Panel value="switches" pt="md"><SwitchesTab onPeople={() => setTab('people')} /></Tabs.Panel>
          </Tabs>
        </Stack>
      </FadeIn>
      <AddPersonModal opened={adding} onClose={() => setAdding(false)} enabledIds={enabledIds} />
    </LayoutContainer>
  );
}

// ─── People ──────────────────────────────────────────────────────────────────

type PersonRow = ConnectorPerson & { id: string };

function PeopleTab({ onAdd, onViewSessions }: { onAdd: () => void; onViewSessions: () => void }) {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [, , setSessionFilters] = usePageFilters('admin/connector/sessions', SESSION_DEFAULTS);
  const { data: people = [], isLoading, error } = useQuery({ queryKey: KEYS.people, queryFn: () => service.getPeople() });
  // The gate can look right (person in the role) and still refuse at sign-in if the role was never
  // linked to the right. Say so here, where the administrator is looking, instead of in a log.
  const { data: gate } = useQuery({ queryKey: [...KEYS.people, 'status'], queryFn: () => service.getPeopleStatus(), staleTime: 60_000 });

  const rows: PersonRow[] = useMemo(
    () => people.filter((p) => matches([p.name, p.email], search)).map((p) => ({ ...p, id: p.userId })),
    [people, search],
  );

  const viewSessions = (p: ConnectorPerson) => {
    setSessionFilters({ ...SESSION_DEFAULTS, status: '', search: p.email || p.name });
    onViewSessions();
  };

  const remove = (p: ConnectorPerson) => {
    modal.confirm({
      title: `Remove ${personLabel(p.name, p.email)} from the connector?`,
      body: p.liveSessions > 0
        ? `They have ${p.liveSessions} live session${p.liveSessions === 1 ? '' : 's'}. Those stop at their next call, and they can no longer sign an AI assistant in.`
        : 'They can no longer sign an AI assistant in to CCMS. Their other roles are untouched.',
      confirmLabel: 'Remove access',
      confirmColor: 'red',
      onConfirm: async () => {
        try {
          await service.removePerson(p.userId);
          toast.success('Access removed', `${personLabel(p.name, p.email)} can no longer use the connector.`);
          queryClient.invalidateQueries({ queryKey: KEYS.people });
          queryClient.invalidateQueries({ queryKey: KEYS.grants });
        } catch (e) {
          toast.error('Could not remove access', e instanceof Error ? e.message : 'Try again.');
        }
      },
    });
  };

  const columns: DataTableColumn<PersonRow>[] = [
    { key: 'name', label: 'Person', sortable: true, render: (_, r) => <PersonCell name={r.name} email={r.email} avatar={r.avatar} /> },
    {
      key: 'liveSessions', label: 'Sessions', sortable: true,
      render: (_, r) => r.totalSessions === 0
        ? <Text size="sm" c="dimmed">Never connected</Text>
        : <Group gap={6}><Badge color={r.liveSessions ? 'green' : 'gray'} variant="light">{r.liveSessions} live</Badge><Text size="xs" c="dimmed">of {r.totalSessions}</Text></Group>,
    },
    { key: 'lastConnectedDateTime', label: 'First connected', sortable: true, render: (v) => formatStamp(v) },
    { key: 'lastUsedDateTime', label: 'Last used', sortable: true, render: (v) => formatRelative(v) },
  ];

  if (error) return <Alert color="red" title="Could not load people">{error instanceof Error ? error.message : 'Try again.'}</Alert>;

  return (
    <Stack gap="md">
      {gate?.problem && (
        <Alert color="red" icon={<IconAlertTriangle size={16} />} title="Nobody can connect until this is fixed">{gate.problem}</Alert>
      )}
      <FilterToolbar search={search} onSearch={setSearch} searchPlaceholder="Search by name or email…" onClearAll={() => setSearch('')}
        rightSection={<Button leftSection={<IconUserPlus size={16} />} onClick={onAdd}>Add person</Button>} />
      <ResultsCount shown={rows.length} total={people.length} noun="people" />
      {!isLoading && people.length === 0 ? (
        <Card withBorder>
          <EmptyState icon={<IconUsers size={36} />} title="Nobody can connect yet" description="Nobody, including administrators, is in the ConnectorUser role until someone adds them. Add the first person to get started." size="md"
            actionLabel="Add person" onAction={onAdd} />
        </Card>
      ) : (
        <Card withBorder p={0}>
          <DataTable<PersonRow> enableInternalSearch={false} columns={columns} data={rows} loading={isLoading} striped highlightOnHover enableInternalSort
            emptyMessage="No one matches that search"
            actions={(r) => (
              <>
                <Menu.Item leftSection={<IconEye size={14} />} disabled={r.totalSessions === 0} onClick={() => viewSessions(r)}>View sessions</Menu.Item>
                <Menu.Divider />
                <Menu.Item color="red" leftSection={<IconUserMinus size={14} />} onClick={() => remove(r)}>Remove access</Menu.Item>
              </>
            )} />
        </Card>
      )}
      <Text size="xs" c="dimmed">Adding a person takes effect the next time they sign in to CCMS. Removing takes effect on their next connector call.</Text>
    </Stack>
  );
}

// ─── Sessions ────────────────────────────────────────────────────────────────

type GrantRow = ConnectorGrant & { id: number };

function SessionsTab() {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState(false);
  const [filters, setFilter, , clearAll] = usePageFilters('admin/connector/sessions', SESSION_DEFAULTS);
  const { data: grants = [], isLoading, isFetching, error } = useQuery({ queryKey: KEYS.grants, queryFn: () => service.getAllGrants(false, 500) });

  const rows: GrantRow[] = useMemo(() => grants
    .filter((g) => matches([g.userName, g.userEmail, g.clientName], filters.search))
    .filter((g) => !filters.status || (filters.status === 'live' ? g.isLive : !g.isLive))
    .filter((g) => !filters.client || (g.clientName ?? g.clientId) === filters.client)
    .filter((g) => !filters.scope || scopeList(g.grantedScopes).includes(filters.scope))
    .map((g) => ({ ...g, id: g.grantID })), [grants, filters]);

  const activeCount = [filters.status, filters.client, filters.scope].filter(Boolean).length;
  const clientOptions = useMemo(() => optionsFrom(grants, (g) => g.clientName || g.clientId), [grants]);
  const scopeOptions = useMemo(() => {
    const all = new Set<string>(); grants.forEach((g) => scopeList(g.grantedScopes).forEach((s) => all.add(s)));
    return [...all].sort().map((s) => ({ value: s, label: scopeLabel(s) }));
  }, [grants]);

  const disconnect = (g: ConnectorGrant) => {
    modal.confirm({
      title: 'Disconnect this session?',
      body: `${personLabel(g.userName, g.userEmail)} will lose access from ${g.clientName || 'this client'} on its next request. They can connect again unless you also remove them from People.`,
      confirmLabel: 'Disconnect',
      confirmColor: 'red',
      onConfirm: async () => {
        try {
          await service.revokeGrant(g.grantID);
          toast.success('Disconnected', `${personLabel(g.userName, g.userEmail)}'s ${g.clientName || 'client'} session is revoked.`);
          queryClient.invalidateQueries({ queryKey: KEYS.grants });
          queryClient.invalidateQueries({ queryKey: KEYS.people });
        } catch (e) {
          toast.error('Could not disconnect', e instanceof Error ? e.message : 'Try again.');
        }
      },
    });
  };

  const columns: DataTableColumn<GrantRow>[] = [
    { key: 'userName', label: 'Person', sortable: true, render: (_, r) => <PersonCell name={r.userName} email={r.userEmail} avatar={r.userAvatar} /> },
    { key: 'clientName', label: 'Client', sortable: true, render: (v, r) => <Text size="sm">{v || r.clientId}</Text> },
    {
      key: 'grantedScopes', label: 'Permissions',
      render: (v) => (
        <Group gap={4}>
          {scopeList(v).map((s) => (
            <Tooltip key={s} label={s}><Badge size="xs" variant="outline" color={isWriteScope(s) ? 'orange' : 'blue'}>{scopeLabel(s)}</Badge></Tooltip>
          ))}
        </Group>
      ),
    },
    { key: 'createdDateTime', label: 'Connected', sortable: true, render: (v) => formatStamp(v) },
    { key: 'lastUsedDateTime', label: 'Last used', sortable: true, render: (v) => formatRelative(v) },
    {
      key: 'isLive', label: 'Status', sortable: true,
      render: (_, r) => r.isLive
        ? <Badge color="green" variant="light">Live</Badge>
        : <Tooltip label={`Revoked ${formatStamp(r.revokedDateTime)}${r.revokedBy ? ` by ${r.revokedBy}` : ''}`}><Badge color="gray" variant="light">Revoked</Badge></Tooltip>,
    },
  ];

  if (error) return <Alert color="red" title="Could not load sessions">{error instanceof Error ? error.message : 'Try again.'}</Alert>;

  return (
    <Stack gap="md">
      <FilterToolbar search={filters.search} onSearch={(v) => setFilter('search', v)} searchPlaceholder="Search by person or client…"
        activeFilterCount={activeCount} onOpenFilters={() => setDrawer(true)} onClearAll={clearAll}
        rightSection={<Button variant="light" leftSection={<IconRefresh size={16} />} loading={isFetching && !isLoading} onClick={() => queryClient.invalidateQueries({ queryKey: KEYS.grants })}>Refresh</Button>} />
      <ResultsCount shown={rows.length} total={grants.length} noun="sessions" />
      {!isLoading && grants.length === 0 ? (
        <Card withBorder><EmptyState icon={<IconPlugConnected size={36} />} title="No sessions" description="Nobody has connected an AI assistant yet." size="md" /></Card>
      ) : (
        <Card withBorder p={0}>
          <DataTable<GrantRow> enableInternalSearch={false} columns={columns} data={rows} loading={isLoading} striped highlightOnHover enableInternalSort
            emptyMessage="No sessions match the filters"
            actions={(r) => r.isLive
              ? <Menu.Item color="red" leftSection={<IconPlugConnectedX size={14} />} onClick={() => disconnect(r)}>Disconnect</Menu.Item>
              : <Menu.Item disabled>Already revoked</Menu.Item>} />
        </Card>
      )}
      <FilterDrawer opened={drawer} onClose={() => setDrawer(false)} onClearAll={clearAll} hasActiveFilters={activeCount > 0 || !!filters.search}>
        <Select label="Status" data={[{ value: 'live', label: 'Live' }, { value: 'revoked', label: 'Revoked' }]} value={filters.status || null} onChange={(v) => setFilter('status', v ?? '')} clearable placeholder="All sessions" />
        <Select label="Client" data={clientOptions} value={filters.client || null} onChange={(v) => setFilter('client', v ?? '')} clearable searchable placeholder="Any client" />
        <Select label="Permission" data={scopeOptions} value={filters.scope || null} onChange={(v) => setFilter('scope', v ?? '')} clearable searchable placeholder="Any permission" />
      </FilterDrawer>
    </Stack>
  );
}

// ─── Audit log ───────────────────────────────────────────────────────────────

type LogRow = ConnectorLog & { id: number };

function resultOf(l: ConnectorLog): 'ok' | 'asked' | 'failed' { return l.success ? 'ok' : l.statusCode === 428 ? 'asked' : 'failed'; }

function AuditTab() {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const [drawer, setDrawer] = useState(false);
  const [filters, setFilter, , clearAll] = usePageFilters('admin/connector/audit', AUDIT_DEFAULTS);
  const { data, isLoading, isFetching, error } = useQuery({ queryKey: KEYS.logs, queryFn: () => service.getLogsRaw({ skip: 0, take: LOG_WINDOW }) });
  const logs = data?.data ?? [];

  const rows: LogRow[] = useMemo(() => logs
    .filter((l) => matches([l.userName, l.userEmail, l.toolName, l.module, l.arguments, l.errorMessage], filters.search))
    .filter((l) => !filters.person || personLabel(l.userName, l.userEmail) === filters.person)
    .filter((l) => !filters.module || l.module === filters.module)
    .filter((l) => !filters.tool || l.toolName === filters.tool)
    .filter((l) => !filters.result || resultOf(l) === filters.result)
    .map((l) => ({ ...l, id: l.connectorLogID })), [logs, filters]);

  const activeCount = [filters.person, filters.module, filters.tool, filters.result].filter(Boolean).length;
  const personOptions = useMemo(() => optionsFrom(logs, (l) => personLabel(l.userName, l.userEmail)), [logs]);
  const moduleOptions = useMemo(() => optionsFrom(logs, (l) => l.module), [logs]);
  const toolOptions = useMemo(() => optionsFrom(logs, (l) => l.toolName), [logs]);

  const columns: DataTableColumn<LogRow>[] = [
    { key: 'logDateTime', label: 'When', sortable: true, render: (v) => <Text size="sm" style={{ whiteSpace: 'nowrap' }}>{formatStamp(v)}</Text> },
    { key: 'userName', label: 'Person', sortable: true, render: (_, r) => <PersonCell name={r.userName} email={r.userEmail} avatar={r.userAvatar} /> },
    { key: 'toolName', label: 'Tool', sortable: true, render: (v) => <Code>{v}</Code> },
    { key: 'module', label: 'Module', sortable: true },
    {
      key: 'success', label: 'Result',
      render: (_, r) => {
        const kind = resultOf(r);
        if (kind === 'ok') return <Badge color="green" variant="light">ok</Badge>;
        if (kind === 'asked') return <Tooltip label="The tool asked the assistant for more input"><Badge color="yellow" variant="light">asked</Badge></Tooltip>;
        return <Tooltip label={r.errorMessage ?? `HTTP ${r.statusCode}`} multiline w={320}><Badge color="red" variant="light">{r.errorMessage ? r.errorMessage.slice(0, 28) : `failed ${r.statusCode}`}</Badge></Tooltip>;
      },
    },
    { key: 'durationMs', label: 'ms', align: 'right', sortable: true },
    {
      key: 'arguments', label: 'Arguments',
      render: (v) => v ? <Tooltip label={String(v)} multiline w={420} disabled={String(v).length <= 90}><Code style={{ whiteSpace: 'nowrap' }}>{String(v).slice(0, 90)}{String(v).length > 90 ? '…' : ''}</Code></Tooltip> : null,
    },
  ];

  if (error) return <Alert color="red" title="Could not load the audit log">{error instanceof Error ? error.message : 'Try again.'}</Alert>;

  return (
    <Stack gap="md">
      <FilterToolbar search={filters.search} onSearch={(v) => setFilter('search', v)} searchPlaceholder="Search person, tool, arguments…"
        activeFilterCount={activeCount} onOpenFilters={() => setDrawer(true)} onClearAll={clearAll}
        rightSection={<Button variant="light" leftSection={<IconRefresh size={16} />} loading={isFetching && !isLoading} onClick={() => queryClient.invalidateQueries({ queryKey: KEYS.logs })}>Refresh</Button>} />
      <ResultsCount shown={rows.length} total={logs.length} noun={data?.hasMore ? `most recent calls (of ${data.totalCount ?? 'more'})` : 'calls'} />
      {!isLoading && logs.length === 0 ? (
        <Card withBorder><EmptyState icon={<IconListDetails size={36} />} title="No activity yet" description="Every tool call an assistant makes, reads included, will appear here." size="md" /></Card>
      ) : (
        <Card withBorder p={0}>
          <DataTable<LogRow> enableInternalSearch={false} columns={columns} data={rows} loading={isLoading} striped highlightOnHover enableInternalSort emptyMessage="No calls match the filters" />
        </Card>
      )}
      <Text size="xs" c="dimmed">Arguments are redacted of free text before they are stored. The page holds the latest {LOG_WINDOW} calls; filters run over that window.</Text>
      <FilterDrawer opened={drawer} onClose={() => setDrawer(false)} onClearAll={clearAll} hasActiveFilters={activeCount > 0 || !!filters.search}>
        <Select label="Person" data={personOptions} value={filters.person || null} onChange={(v) => setFilter('person', v ?? '')} clearable searchable placeholder="Anyone" />
        <Select label="Module" data={moduleOptions} value={filters.module || null} onChange={(v) => setFilter('module', v ?? '')} clearable placeholder="Any module" />
        <Select label="Tool" data={toolOptions} value={filters.tool || null} onChange={(v) => setFilter('tool', v ?? '')} clearable searchable placeholder="Any tool" />
        <Select label="Result" data={[{ value: 'ok', label: 'Succeeded' }, { value: 'failed', label: 'Failed' }, { value: 'asked', label: 'Asked for input' }]} value={filters.result || null} onChange={(v) => setFilter('result', v ?? '')} clearable placeholder="Any result" />
      </FilterDrawer>
    </Stack>
  );
}

// ─── Clients ─────────────────────────────────────────────────────────────────

type ClientRow = ConnectorClient & { id: number };

function ClientsTab() {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const { data: clients = [], isLoading, isFetching, error } = useQuery({ queryKey: KEYS.clients, queryFn: () => service.getClients() });
  const rows: ClientRow[] = useMemo(() => clients.filter((c) => matches([c.clientName, c.clientId], search)).map((c) => ({ ...c, id: c.clientRowID })), [clients, search]);

  const deactivate = (client: ConnectorClient) => {
    modal.confirm({
      title: `Deactivate "${client.clientName}"?`,
      body: 'It will no longer be able to sign anyone in, and every session it currently holds is disconnected immediately.',
      confirmLabel: 'Deactivate',
      confirmColor: 'red',
      onConfirm: async () => {
        try {
          await service.deactivateClient(client.clientId);
          toast.success('Client deactivated', `${client.clientName} can no longer sign anyone in.`);
          queryClient.invalidateQueries({ queryKey: KEYS.clients });
          queryClient.invalidateQueries({ queryKey: KEYS.grants });
        } catch (e) {
          toast.error('Could not deactivate', e instanceof Error ? e.message : 'Try again.');
        }
      },
    });
  };

  const columns: DataTableColumn<ClientRow>[] = [
    { key: 'clientName', label: 'Name', sortable: true, render: (v) => <Text size="sm" fw={500}>{v}</Text> },
    { key: 'clientId', label: 'Client id', render: (v) => <Code>{v}</Code> },
    { key: 'isDynamic', label: 'Registered', render: (v) => (v ? 'By the client itself' : 'Manually') },
    { key: 'createdDateTime', label: 'Since', sortable: true, render: (v) => formatStamp(v) },
    { key: 'isActive', label: 'Status', sortable: true, render: (v) => (v ? <Badge color="green" variant="light">Active</Badge> : <Badge color="gray" variant="light">Inactive</Badge>) },
  ];

  if (error) return <Alert color="red" title="Could not load clients">{error instanceof Error ? error.message : 'Try again.'}</Alert>;

  return (
    <Stack gap="md">
      <FilterToolbar search={search} onSearch={setSearch} searchPlaceholder="Search clients…" onClearAll={() => setSearch('')}
        rightSection={<Button variant="light" leftSection={<IconRefresh size={16} />} loading={isFetching && !isLoading} onClick={() => queryClient.invalidateQueries({ queryKey: KEYS.clients })}>Refresh</Button>} />
      <ResultsCount shown={rows.length} total={clients.length} noun="clients" />
      {!isLoading && clients.length === 0 ? (
        <Card withBorder><EmptyState icon={<IconApps size={36} />} title="No clients" description="No AI client has registered with the connector yet. One appears the first time someone adds the connector in their assistant." size="md" /></Card>
      ) : (
        <Card withBorder p={0}>
          <DataTable<ClientRow> enableInternalSearch={false} columns={columns} data={rows} loading={isLoading} striped highlightOnHover enableInternalSort emptyMessage="No clients match"
            actions={(r) => r.isActive
              ? <Menu.Item color="red" leftSection={<IconPower size={14} />} onClick={() => deactivate(r)}>Deactivate</Menu.Item>
              : <Menu.Item disabled>Inactive</Menu.Item>} />
        </Card>
      )}
    </Stack>
  );
}

// ─── Switches ────────────────────────────────────────────────────────────────

function SwitchesTab({ onPeople }: { onPeople: () => void }) {
  const service = useConnectorService();
  const queryClient = useQueryClient();
  const { data: settings = [], isLoading, error } = useQuery({ queryKey: KEYS.settings, queryFn: () => service.getSettings() });

  const master = settings.find((s) => s.name === 'Enabled');
  const modules = settings.filter((s) => s.name.startsWith('Module.'));

  const flip = async (setting: ConnectorSetting, on: boolean) => {
    try {
      await service.setSetting(setting.name, on ? 'true' : 'false');
      toast.success(on ? 'Switched on' : 'Switched off', `${describe(setting)} ${on ? 'is now on' : 'is now off'}.`);
      queryClient.invalidateQueries({ queryKey: KEYS.settings });
    } catch (e) {
      toast.error('Could not change the switch', e instanceof Error ? e.message : 'Try again.');
    }
  };

  const onToggle = (setting: ConnectorSetting, on: boolean) => {
    // Turning the master ON is the significant act — it is what makes anyone's session work.
    if (setting.name === 'Enabled' && on) {
      modal.confirm({
        title: 'Switch the connector on?',
        body: 'Everyone on the People tab will be able to connect and act through an AI assistant, within their own CCMS rights.',
        confirmLabel: 'Switch on',
        onConfirm: () => flip(setting, true),
      });
      return;
    }
    void flip(setting, on);
  };

  if (isLoading) return <Group justify="center" py="xl"><Loader /></Group>;
  if (error) return <Alert color="red" title="Could not load switches">{error instanceof Error ? error.message : 'Try again.'}</Alert>;

  return (
    <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
      <Stack gap="md">
        {master && (
          <Card withBorder radius="md" p="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <div>
                <Group gap="xs">
                  <Text fw={600}>Connector enabled</Text>
                  <Badge color={master.isOn ? 'green' : 'red'} variant="light">{master.isOn ? 'ON' : 'OFF'}</Badge>
                </Group>
                <Text size="sm" c="dimmed">Master switch. When off, every tool call is refused regardless of the module switches.</Text>
              </div>
              <Switch size="lg" checked={master.isOn} onChange={(e) => onToggle(master, e.currentTarget.checked)} />
            </Group>
          </Card>
        )}
        <Alert color="blue" icon={<IconUsers size={16} />} title="Who may connect">
          Switches control the surface, not the people. Someone can connect only once they are on the{' '}
          <Button variant="subtle" size="compact-sm" onClick={onPeople}>People</Button> tab — nobody, including administrators, is there until an administrator adds them.
        </Alert>
      </Stack>

      <Card withBorder radius="md" p="md">
        <Text fw={600} mb="xs">Modules</Text>
        <Text size="xs" c="dimmed" mb="md">Each switch pulls one group of tools. Sessions keep working; the tools in that group refuse on the next call.</Text>
        <Stack gap="sm">
          {modules.map((m) => (
            <Group key={m.settingID} justify="space-between" wrap="nowrap">
              <div>
                <Text size="sm">{m.name.replace('Module.', '')}</Text>
                {m.description && <Text size="xs" c="dimmed">{m.description}</Text>}
              </div>
              <Switch checked={m.isOn} disabled={!master?.isOn} onChange={(e) => onToggle(m, e.currentTarget.checked)} />
            </Group>
          ))}
        </Stack>
      </Card>
    </SimpleGrid>
  );
}

function describe(setting: ConnectorSetting) {
  return setting.name === 'Enabled' ? 'The connector' : setting.name.replace('Module.', '') + ' tools';
}
